// src/lib/seo.js
import React from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { SITE, url } from "./site"; // central config

// Back-compat
export const ORIGIN = SITE;

// Tiny utils (normalized, no trailing slash on canonical)
export const abs = (path = "/") => url.abs(path);
export const canonical = (path = "/") => url.canonical(path).replace(/\/$/, "");

export const PRIMARY_HOST = (() => {
  try { return new URL(SITE).host; } catch { return ""; }
})();

export function isPreviewHost() {
  if (typeof window === "undefined") return false;
  try {
    return new URL(window.location.href).host !== PRIMARY_HOST;
  } catch {
    return false;
  }
}

// Title helper: append site name once
const withSite = (t) => {
  if (!t) return undefined;
  const siteName = "Party Crafters";
  return /\s\|\s|Party Crafters/i.test(t) ? t : `${t} | ${siteName}`;
};

export function Seo({
  title,
  description,
  // Pass a path like "/services/backdrops" and we’ll compute canonical
  path,
  canonical: canonicalProp,     // optional: absolute canonical to override
  noindex,                      // optional: force noindex
  ogImage,                      // absolute or path; we’ll absolutize if needed
  ogType = "website",
  twitterCard = "summary_large_image",
  jsonLd = [],
}) {
  const computedFromPath =
    path
      ? canonical(path)
      : (typeof window !== "undefined" ? canonical(window.location.pathname) : undefined);

  const canonicalHref = (canonicalProp || computedFromPath) || undefined;

  const robots =
    noindex || isPreviewHost() ? "noindex,nofollow" : "index,follow";

  const titled = withSite(title);
  const ogUrl = canonicalHref || (path ? abs(path) : undefined);
  const ogImg = ogImage ? abs(ogImage) : undefined;

  return (
    <Helmet>
      {titled && <title>{titled}</title>}
      {description && <meta name="description" content={description} />}

      {canonicalHref && <link rel="canonical" href={canonicalHref} />}
      <meta name="robots" content={robots} />

      {/* Open Graph */}
      {titled && <meta property="og:title" content={titled} />}
      {description && <meta property="og:description" content={description} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:site_name" content="Party Crafters" />
      {ogImg && <meta property="og:image" content={ogImg} />}
      {ogImg && <meta property="og:image:alt" content={description || titled || "Party Crafters event décor"} />}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      {titled && <meta name="twitter:title" content={titled} />}
      {description && <meta name="twitter:description" content={description} />}
      {ogImg && <meta name="twitter:image" content={ogImg} />}

      {jsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
